/* eslint-disable max-len */
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import React, { FC, useEffect, useMemo, useRef, useState } from 'react'
import Modal from '../common/Modal'
import { dateManipulateDay, dateToTodayElapsedSeconds, format, nowDate, secToHour, secToMinute, secToSec } from '../../../utils/date'
import { labelColor } from '../../../utils/taskStatusLabel'
import { Link, useHistory } from 'react-router-dom'
import { URL } from '../../constants/url'
import { setPath, setUrlId } from '../../../utils/url'
import { sortBy } from 'lodash'
import { useSelector } from 'react-redux'
import { RootState } from '../../../states/containers/store'
import LoadingSquare from '../common/LoadingSquare'
import { nl2br } from '../../../utils/string'
import Tips from '../common/Tips'
import SvgPieChart from './SvgPieChart'
import { IconName, IconPrefix } from '@fortawesome/fontawesome-common-types'

interface UpdateParam {
  member_id: number
  status: string
  comment: string
  target_date: string
  work_start_time?: string
  work_end_time?: string
}

interface WorkTime {
  start_hour: number
  start_minutes: number
  end_hour: number
  end_minutes: number
}

interface Props {
  detail: {
    member_id: number
    member_name: string
    target_date: string
  }
  closeCallback: VoidFunction
  updateCallback: (param: UpdateParam, callback: VoidFunction) => void
  isDisplay: boolean
  closeable?: boolean
  isApplication?: boolean
  displayLink?: boolean
  moveDateCallback?: (addNum: number) => void
  isDisplayWorkTime?: boolean
}

const ApprovedModal: FC<Props> = (props) => {
  const {
    detail,
    closeCallback,
    updateCallback,
    isDisplay,
    closeable,
    isApplication,
    displayLink,
    moveDateCallback,
    isDisplayWorkTime,
  } = props

  const {
    task,
    login,
  } = useSelector((state: RootState) => state)

  const history = useHistory()

  const commentRef = useRef() as React.MutableRefObject<HTMLInputElement>
  const [selectStatus, setSelectStatus] = useState<string>("")
  const [comment, setComment] = useState<string>("")

  const [isOpenFlow, setIsOpenFlow] = useState<boolean>(true)
  const [isOpenOutside, setIsOpenOutside] = useState<boolean>(true)

  const [startTimeSec, setStartTimeSec] = useState<number>(18000)
  const [endTimeSec, setEndTimeSec] = useState<number>(86400 + 18000)
  const [overTimeSec, setOverTimeSec] = useState<number>(0)

  const [isSelectRealTime, setIsSelectRealTime] = useState<boolean>(false)

  const [recodeSeconds, setRecordSeconds] = useState<number>(0)

  const [pieChartType, setPieChartType] = useState<string>("labeling") // labeling, project, work
  const [pieChartLabel, setPieChartLabel] = useState<string>("仕分け率")
  const [pieChartList, setPieChartList] = useState<{ label: string, value: number, color: string }[]>([])

  const [isSubmitLoading, setIsSubmitLoading] = useState<boolean>(false)

  const detailData = useMemo(() => {
    if (!task.taskApprovalApplicationDetail) return null
    return (
      {
        ...task.taskApprovalApplicationDetail,
        member_id: detail.member_id,
        member_name: detail.member_name,
        target_date: detail.target_date,
      }
    )
  }, [task.taskApprovalApplicationDetail])

  const selectList = useMemo(() => {
    if (!detailData) return ([])
    if (
      isApplication
      && (
        (detailData.approval_status === "unapplied")
        || (detailData.approval_status === "returned")
        || (detailData.approval_status === "dropped")
        || (detailData.approval_status === "canceled")
      )
    ) {
      return (
        [
          {
            key: "processing",
            text: "申請中",
          },
        ]
      )
    }
    if (detailData.approval_status === "completed") {
      return (
        [
          {
            key: "dropped",
            text: "承認取消",
          },
        ]
      )
    }
    if (
      detailData.approval_status === "processing"
      && detailData.member_id === login.current_member_id
      && detailData.approvers.filter((v) => v.member_id === login.current_member_id).length
    ) {
      return (
        [
          {
            key: "approved",
            text: "承認済み",
          },
          {
            key: "canceled",
            text: "申請取消",
          },
        ]
      )
    }
    if (!isApplication && detailData.approval_status === "processing") {
      return (
        [
          {
            key: "approved",
            text: "承認済み",
          },
          {
            key: "returned",
            text: "差戻し",
          },
        ]
      )
    }
    if (isApplication && detailData.approval_status === "processing") {
      return (
        [
          {
            key: "canceled",
            text: "申請取消",
          },
        ]
      )
    }
    if (
      (detailData.approval_status === "unapplied")
      || (detailData.approval_status === "returned")
      || (detailData.approval_status === "dropped")
    ) {
      return (
        [
          {
            key: "approved",
            text: "承認済み",
          },
        ]
      )
    }
    return ([])
  }, [detailData, isApplication])

  const isApply = useMemo(() => {
    if (!detailData) return false
    if (!detailData.approvers.length) return false
    if (
      !isApplication
      && (
        detailData.approval_status === "approved"
        || detailData.approval_status === "skipped"
        || detailData.approval_status === "unapplied"
        || detailData.approval_status === "canceled"
        || detailData.approval_status === "returned"
        || detailData.approval_status === "dropped"
      )
    ) {
      return false
    }
    if (
      !isApplication
      && detailData.approval_status === "completed"
      && !detailData.is_current_last_approver
    ) {
      return false
    }
    if (
      isApplication
      && detailData.approval_status !== "unapplied"
      && detailData.approval_status !== "returned"
      && detailData.approval_status !== "dropped"
      && detailData.approval_status !== "processing"
      && detailData.approval_status !== "canceled"
    ) {
      return false
    }
    if (
      isApplication
      && detailData.approval_status === "processing"
      && detailData.approvers.filter((v) => v.status === "approved").length
    ) {
      return false
    }
    return true
  }, [detailData, startTimeSec, endTimeSec])

  const overtime = useMemo(() => {
    if (
      (dateToTodayElapsedSeconds(detailData?.labeling_status.start_time || "", detail.target_date) < startTimeSec)
      || (dateToTodayElapsedSeconds(detailData?.labeling_status.end_time || "", detail.target_date) > endTimeSec + 60)
    ) {
      return true
    }
    return false
  }, [startTimeSec, endTimeSec])

  const breakTimeSec = useMemo(() => (
    detailData?.labeling_status.break_time || 0
  ), [detailData])

  const buildSecToTime = (sec: number) => (
    <>
      {
        !!Number(secToHour(sec)) && (
          <>
            {`${secToHour(sec)}時間`}
          </>
        )
      }
      {
        Number(secToMinute(sec)) ? (
          <>
            {`${secToMinute(sec)}分`}
          </>
        ) : (
          <>
            0分
          </>
        )
      }
      {
        !!Number(secToSec(sec)) && (
          <>
            {`${secToSec(sec)}秒`}
          </>
        )
      }
    </>
  )

  const buildStatusWord = (status: string) => {
    if (status === "processing") {
      return "申請"
    }
    if (status === "canceled") {
      return "申請取消"
    }
    if (status === "dropped") {
      return "承認取消"
    }
    if (status === "approved") {
      return "承認"
    }
    if (status === "returned") {
      return "差戻"
    }
    return ""
  }

  const buildStatus = (status: string, short = false) => {
    let tmpDom = (<></>)
    if (status === "approved") {
      if (short) return (<>承認済み</>)
      tmpDom = (
        <>
          <FontAwesomeIcon icon={['fas', 'check']} />
          承認済み
        </>
      )
    } else if (status === "completed") {
      if (short) return (<>承認完了</>)
      tmpDom = (
        <>
          <FontAwesomeIcon icon={['fas', 'check']} />
          承認完了
        </>
      )
    } else if (status === "processing") {
      if (short) return (<>申請中</>)
      tmpDom = (
        <>
          <FontAwesomeIcon icon={['far', 'clock']} />
          申請中
        </>
      )
    } else if (status === "skipped") {
      if (short) return (<>スキップ</>)
      tmpDom = (
        <>
          <FontAwesomeIcon icon={['fas', 'exclamation-triangle']} />
          スキップ
        </>
      )
    } else if (status === "unapplied" || status === "canceled") {
      if (short) return (<>未申請</>)
      tmpDom = (
        <>
          <FontAwesomeIcon icon={['fas', 'exclamation-triangle']} />
          未申請
        </>
      )
    } else if (status === "returned") {
      if (short) return (<>差戻し</>)
      tmpDom = (
        <>
          <FontAwesomeIcon icon={['fas', 'exclamation-triangle']} />
          差戻し
        </>
      )
    } else if (status === "dropped") {
      if (short) return (<>承認取消</>)
      tmpDom = (
        <>
          <FontAwesomeIcon icon={['fas', 'exclamation-triangle']} />
          承認取消
        </>
      )
    }
    return (
      <>
        <div
          className="approved_modal_label"
          style={{
            color: labelColor(status),
            border: `solid 1px ${labelColor(status)}`,
            backgroundColor: labelColor(status, true),
          }}
        >
          {tmpDom}
        </div>
      </>
    )
  }

  const statusApproverColor = (status: string) => {
    if (status === 'approved') {
      return "#0f9655"
    }
    if (status === 'skipped') {
      return "#d47e13"
    }
    return "#FF0000"
  }

  const buildStatusApprover = (status: string) => {
    if (status === "approved") {
      return (
        <>
          <FontAwesomeIcon icon={['fas', 'check']} />
          承認完了
        </>
      )
    }
    if (status === "skipped") {
      return (
        <>
          <FontAwesomeIcon icon={['fas', 'exclamation-triangle']} />
          スキップ
        </>
      )
    }
    return (
      <>
        <FontAwesomeIcon icon={['fas', 'exclamation-triangle']} />
        未承認
      </>
    )
  }

  const closeModal = () => {
    closeCallback()
    setComment("")
  }

  const formatSecToDateTime = (sec: number) => {
    if (sec > 86400) {
      const tmpSec = sec - 86400
      return `${dateManipulateDay(detail.target_date, 1, "YYYY-MM-DD")} ${`00${secToHour(tmpSec)}`.slice(-2)}:${`00${secToMinute(tmpSec)}`.slice(-2)}:${`00${secToSec(tmpSec)}`.slice(-2)}`
    }
    return `${detail.target_date} ${`00${secToHour(sec)}`.slice(-2)}:${`00${secToMinute(sec)}`.slice(-2)}:${`00${secToSec(sec)}`.slice(-2)}`
  }

  const submitApproval = () => {
    setIsSubmitLoading(true)
    let param: UpdateParam = {
      member_id: detailData?.member_id || 0,
      status: selectStatus,
      comment: overtime ? `[システムメッセージ]勤務時間外に仕分け済み作業データが存在します\n${comment}` : comment,
      target_date: detailData?.target_date || "",
      work_start_time: formatSecToDateTime(startTimeSec) || detailData?.labeling_status.start_time || "",
    }
    if (selectStatus === "processing") {
      if (!isSelectRealTime) {
        param = {
          ...param,
          work_start_time: formatSecToDateTime(startTimeSec) || detailData?.labeling_status.start_time || "",
          work_end_time: formatSecToDateTime(endTimeSec) || detailData?.labeling_status.end_time || "",
        }
      } else {
        param = { ...param, work_start_time: formatSecToDateTime(startTimeSec) || detailData?.labeling_status.start_time || "" }
      }
    }
    updateCallback(
      param,
      () => {
        setIsSubmitLoading(false)
        if (!moveDateCallback) {
          closeModal()
        }
      },
    )
  }

  const buildDisplayTime = () => {
    if (!detailData) return ""
    const start = format(detailData.labeling_status.start_time, "YYYY/MM/DD HH:mm:ss")
    const end = format(detailData.labeling_status.end_time, "YYYY/MM/DD HH:mm:ss")
    return `${start} ~ ${end}`
  }

  useEffect(() => {
    if (detailData?.approval_status === "completed") {
      setSelectStatus("dropped")
      return
    }
    if (detailData?.approval_status === "processing" && detailData?.approvers.filter((v) => v.member_id === login.current_member_id).length) {
      setSelectStatus("approved")
      return
    }
    if (isApplication && detailData?.approval_status === "processing") {
      setSelectStatus("canceled")
      return
    }
    if (isApplication) {
      setSelectStatus("processing")
      return
    }
    setSelectStatus("approved")
  }, [isDisplay, detailData])

  useEffect(() => {
    if (isDisplay && !task.isLoadingForModal) {
      setTimeout(() => {
        commentRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' })
      }, 1000)
    }
    setPieChartType("labeling")
  }, [isDisplay, task.isLoadingForModal])

  useEffect(() => {
    if (!detailData?.labeling_status.start_time || !detailData?.labeling_status.end_time) {
      setStartTimeSec(18000)
      setEndTimeSec(86400 + 18000)
      setOverTimeSec(0)
      return
    }
    const tmpStartTime = dateToTodayElapsedSeconds(detailData.labeling_status.start_time, detail.target_date)
    const tmpEndTime = dateToTodayElapsedSeconds(detailData.labeling_status.end_time, detail.target_date)
    setStartTimeSec(tmpStartTime)
    setEndTimeSec(tmpEndTime)
    setOverTimeSec(detailData.labeling_status.over_time)
    if (format(detailData.target_date, "YYYYMMDD") === nowDate("YYYYMMDD")) {
      setIsSelectRealTime(true)
    } else {
      setIsSelectRealTime(false)
    }
  }, [detailData])

  useEffect(() => {
    const timeId = setTimeout(() => {
      const d = new Date()
      const seconds = d.getSeconds()
      setRecordSeconds(seconds)
    }, 1000)
    return () => clearTimeout(timeId)
  }, [recodeSeconds])

  useEffect(() => {
    if (pieChartType === "labeling") {
      setPieChartList([
        {
          label: "仕分け済み",
          value: detailData?.labeling_status.labeling || 0,
          color: "#0D9554",
        },
        {
          label: "未仕分け",
          value: detailData?.labeling_status.not_labeling || 0,
          color: "#c61616",
        },
      ])
      setPieChartLabel("仕分け率")
      return
    }
    if (pieChartType === "project") {
      setPieChartList(detailData?.label_groups.filter((v) => v.name !== "休憩" && v.name !== "プロジェクトに属さない作業データ").map((v) => ({ label: v.name, value: v.time, color: v.color })) || [])
      setPieChartLabel("プロジェクト比率")
      return
    }
    if (pieChartType === "work") {
      setPieChartList(detailData?.labels.filter((v) => v.name !== "休憩" && v.name !== "プロジェクトに属さない作業データ").map((v) => ({ label: v.name, value: v.time, color: v.color })) || [])
      setPieChartLabel("業務比率")
    }
  }, [pieChartType, detailData])

  return (
    <Modal
      show={isDisplay}
      wrapperClass="approved_modal w900"
      closeCallback={closeModal}
      closeable={closeable}
    >
      <>
        {
          !detailData || task.isLoadingForModal ? (
            <div
              style={{
                minHeight: isApply ? "602px" : "506px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexFlow: "column",
              }}
            >
              <LoadingSquare>
                <>
                  情報を取得しています…
                </>
              </LoadingSquare>
            </div>
          ) : (
            <>
              <div
                className="approved_modal_header"
                style={{
                  justifyContent: moveDateCallback ? "space-between" : "center",
                }}
              >
                {
                  moveDateCallback && (
                    Number(format(detail.target_date, "YYYYMMDD")) > 20240501 ? (
                      <button
                        type="button"
                        onClick={() => moveDateCallback(-1)}
                      >
                        <FontAwesomeIcon icon={['fas', 'angle-left']} />
                        前日
                      </button>
                    ) : (
                      <div style={{ width: "58px" }} />
                    )
                  )
                }
                <div className="approved_modal_title">
                  {`${format(detail.target_date, "YYYY/MM/DD")} ${detail.member_name}さんの申請状況詳細`}
                </div>
                {
                  moveDateCallback && (
                    Number(format(detail.target_date, "YYYYMMDD")) < Number(nowDate("YYYYMMDD")) ? (
                      <button
                        type="button"
                        onClick={() => moveDateCallback(1)}
                      >
                        翌日
                        <FontAwesomeIcon icon={['fas', 'angle-right']} />
                      </button>
                    ) : (
                      <div style={{ width: "58px" }} />
                    )
                  )
                }
              </div>
              {
                detailData.labeling_status.changed && (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "2px",
                      borderRadius: "4px",
                      padding: "4px 8px",
                      backgroundColor: "#f5e41b",
                      marginBottom: "16px",
                    }}
                  >
                    勤務開始・終了時刻の変更に関係する作業データ、または、業務外指定範囲設定があります。
                    {
                      !isApplication && (
                        <>
                          <Link
                            to={`${setPath(setUrlId(URL.TASK_LABELING.path, 'id', detailData.member_id), "ja")}?date=${format(detailData.target_date, "YYYY-MM-DD")}`}
                          >
                            こちら
                          </Link>
                          から仕分け詳細を確認してください。
                        </>
                      )
                    }
                  </div>
                )
              }
              <div className="approved_modal_summary_area">
                <div>
                  <div className="status_area">
                    <div>
                      申請状況:
                    </div>
                    {buildStatus(detailData.approval_status)}
                  </div>
                  <div className="chart_area">
                    <SvgPieChart
                      list={pieChartList}
                      graphTitle=""
                      chartSize={200}
                      displayOuterLabel
                    />
                    <div className="text_area">
                      <div className="text_area_title">
                        {pieChartLabel}
                      </div>
                      {
                        pieChartType === "labeling" && (
                          <div className="text_area_text">
                            {
                              detailData.labeling_status.labeling ? (
                                <>
                                  {`${Math.floor((detailData.labeling_status.labeling / (detailData.labeling_status.labeling + detailData.labeling_status.not_labeling)) * 1000) / 10}%`}
                                </>
                              ) : (
                                <>
                                  0%
                                </>
                              )
                            }
                          </div>
                        )
                      }
                    </div>
                  </div>
                  <div className="chart_button_area">
                    <button
                      className={pieChartType === "labeling" ? "on" : ""}
                      type="button"
                      onClick={() => setPieChartType("labeling")}
                    >
                      仕分け率
                    </button>
                    <button
                      className={pieChartType === "project" ? "on" : ""}
                      type="button"
                      onClick={() => setPieChartType("project")}
                    >
                      プロジェクト比率
                    </button>
                    <button
                      className={pieChartType === "work" ? "on" : ""}
                      type="button"
                      onClick={() => setPieChartType("work")}
                    >
                      業務比率
                    </button>
                  </div>
                  {
                    displayLink && (
                      <>
                        {
                          detailData.view_labeling_board ? (
                            <button
                              type="button"
                              onClick={() => history.push(`${setPath(setUrlId(URL.TASK_LABELING.path, 'id', detailData.member_id), "ja")}?date=${format(detailData.target_date, "YYYY-MM-DD")}`)}
                              style={{
                                border: "solid 1px #999999",
                                borderRadius: "4px",
                                padding: "4px",
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                gap: "4px",
                                backgroundColor: "#FFFFFF",
                                color: "#666666",
                                cursor: "pointer",
                                userSelect: "none",
                                width: "100%",
                              }}
                            >
                              <FontAwesomeIcon icon={['fas', 'tasks']} />
                              業務仕分けボードを見る
                            </button>
                          ) : (
                            <Tips
                              description="業務仕分けボードを見る権限がありません"
                              className=""
                              style={{ zIndex: 2010 }}
                              icon={false}
                            >
                              <div
                                style={{
                                  border: "solid 1px #999999",
                                  borderRadius: "4px",
                                  padding: "4px",
                                  display: "flex",
                                  justifyContent: "center",
                                  alignItems: "center",
                                  gap: "4px",
                                  backgroundColor: "#EEEEEE",
                                  color: "#999999",
                                  cursor: "not-allowed",
                                  userSelect: "none",
                                }}
                              >
                                <FontAwesomeIcon icon={['fas', 'tasks']} />
                                業務仕分けボードを見る
                              </div>
                            </Tips>
                          )
                        }
                      </>
                    )
                  }
                </div>
                <div className="data_area">
                  {
                    isDisplayWorkTime && (
                      <div className="text_area">
                        <div>
                          勤務時間:
                        </div>
                        {buildDisplayTime()}
                      </div>
                    )
                  }
                  <div className="summary_area">
                    <div className="summary_area_title">
                      仕分けサマリー
                    </div>
                    <div className="summary_area_main">
                      <div className="label_chip_area">
                        <div
                          style={{
                            width: "calc(100% / 3)",
                          }}
                        >
                          <Tips
                            description="勤務開始~終了時間から休憩時間と業務内にある業務時間外範囲時間を引いた時間"
                            className=""
                            style={{ zIndex: 2010 }}
                            icon={false}
                          >
                            <>
                              <div
                                className="label_chip"
                                style={{
                                  backgroundColor: "#2196f3",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                実労働時間
                              </div>
                            </>
                          </Tips>
                          <div className="label_area_value">
                            {
                              detailData.labeling_status.total_work_time >= 0 ? (
                                <>
                                  {buildSecToTime(detailData.labeling_status.total_work_time)}
                                </>
                              ) : (
                                <>
                                  実労働時間なし
                                </>
                              )
                            }
                          </div>
                        </div>
                        <div
                          style={{
                            width: "calc(100% / 3)",
                          }}
                        >
                          <Tips
                            description="休憩ラベルをつけた作業データの合計時間"
                            className=""
                            style={{ zIndex: 2010 }}
                            icon={false}
                          >
                            <>
                              <div
                                className="label_chip"
                                style={{
                                  backgroundColor: "#e88042",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                休憩時間
                              </div>
                            </>
                          </Tips>
                          <div className="label_area_value">
                            {
                              breakTimeSec ? (
                                <>
                                  {buildSecToTime(breakTimeSec)}
                                </>
                              ) : (
                                <>
                                  休憩なし
                                </>
                              )
                            }
                          </div>
                        </div>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "flex-end",
                            gap: "4px",
                            width: "calc(100% / 3)",
                          }}
                        >
                          <Tips
                            description="実労働時間から標準勤務時間を引いた時間"
                            className=""
                            style={{ zIndex: 2010 }}
                            icon={false}
                          >
                            <>
                              <div
                                className="label_chip"
                                style={{
                                  backgroundColor: "#7d0000",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                残業時間
                              </div>
                            </>
                          </Tips>
                          <div className="label_area_value">
                            {
                              overTimeSec > 0 ? (
                                <>
                                  {buildSecToTime(overTimeSec)}
                                </>
                              ) : (
                                <>
                                  残業なし
                                </>
                              )
                            }
                          </div>
                        </div>
                      </div>
                      <div className="label_bar" />
                      <div className="label_chip_area">
                        <div
                          style={{
                            width: "50%",
                          }}
                        >
                          <div
                            className="label_chip"
                            style={{
                              backgroundColor: "#0c9554",
                              whiteSpace: "nowrap",
                            }}
                          >
                            仕分け済み時間
                          </div>
                          <div className="label_area_value">
                            {
                              detailData.labeling_status.labeling ? (
                                <>
                                  {buildSecToTime(detailData.labeling_status.labeling)}
                                  {`(${Math.round((detailData.labeling_status.labeling / (detailData.labeling_status.labeling + detailData.labeling_status.not_labeling)) * 1000) / 10}%)`}
                                </>
                              ) : (
                                <>
                                  仕分けデータがありません
                                </>
                              )
                            }
                          </div>
                        </div>
                        <div
                          style={{
                            width: "50%",
                          }}
                        >
                          <div
                            className="label_chip"
                            style={{
                              backgroundColor: "#c61616",
                              whiteSpace: "nowrap",
                            }}
                          >
                            未仕分け時間
                          </div>
                          <div className="label_area_value">
                            {
                              detailData.labeling_status.labeling ? (
                                <>
                                  {buildSecToTime(detailData.labeling_status.not_labeling)}
                                  {`(${Math.round((detailData.labeling_status.not_labeling / (detailData.labeling_status.labeling + detailData.labeling_status.not_labeling)) * 1000) / 10}%)`}
                                </>
                              ) : (
                                <>
                                  仕分けデータがありません
                                </>
                              )
                            }
                          </div>
                        </div>
                      </div>
                      <div className="label_bar" />
                      <div className="label_chip_area">
                        <div
                          style={{
                            width: "50%",
                          }}
                        >
                          <Tips
                            description="業務時間外範囲の合計時間"
                            className=""
                            style={{ zIndex: 2010 }}
                            icon={false}
                          >
                            <>
                              <div
                                className="label_chip"
                                style={{
                                  backgroundColor: "#4A4A4A",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                業務時間外範囲の合計時間
                              </div>
                            </>
                          </Tips>
                          <div className="label_area_value">
                            {
                              detailData.labeling_status.not_working_time && detailData.labeling_status.not_working_time >= 0 ? (
                                <>
                                  {buildSecToTime(detailData.labeling_status.not_working_time)}
                                </>
                              ) : (
                                <>
                                  なし
                                </>
                              )
                            }
                          </div>
                        </div>
                        {
                          (isApplication) && (
                            <>
                              <div
                                style={{
                                  width: "50%",
                                }}
                              >
                                <Tips
                                  description="業務時間内で手動登録可能な時間の合計"
                                  className=""
                                  style={{ zIndex: 2010 }}
                                  icon={false}
                                >
                                  <>
                                    <div
                                      className="label_chip"
                                      style={{
                                        backgroundColor: "#00187d",
                                        whiteSpace: "nowrap",
                                      }}
                                    >
                                      業務時間内の手動登録可能時間
                                    </div>
                                  </>
                                </Tips>
                                <div className="label_area_value">
                                  {
                                    detailData.labeling_status.task_creatable_time ? (
                                      <>
                                        {
                                          detailData.labeling_status.task_creatable_time >= 0 ? (
                                            <>
                                              {buildSecToTime(detailData.labeling_status.task_creatable_time)}
                                            </>
                                          ) : (
                                            <>
                                              手動登録可能時間はありません
                                            </>
                                          )
                                        }
                                      </>
                                    ) : (
                                      <>
                                        なし
                                      </>
                                    )
                                  }
                                </div>
                              </div>
                            </>
                          )
                        }
                      </div>
                    </div>
                  </div>
                  <div className="log_area">
                    <div className="log_area_title">
                      変更履歴
                    </div>
                    <div className="log_area_main">
                      {
                        detailData.histories.map((v) => (
                          <div
                            key={`${v.member_id}_${v.history_id}`}
                            className="log_block"
                          >
                            <div className="log_block_title">
                              {`${format(v.created_at, "YYYY/MM/DD HH:mm:ss")} ${v.name}`}
                            </div>
                            <div className="log_block_status">
                              {buildStatus(v.prev_status)}
                              <FontAwesomeIcon icon={['fas', 'arrow-right']} />
                              {buildStatus(v.status)}
                            </div>
                            {/* eslint-disable-next-line react/no-danger */}
                            <div dangerouslySetInnerHTML={{ __html: nl2br(v.comment) }} />
                          </div>
                        ))
                      }
                      {
                        !detailData.histories.length && (
                          <div className="no_data">
                            履歴はありません
                          </div>
                        )
                      }
                      <div ref={commentRef} />
                    </div>
                  </div>
                </div>
              </div>
              {
                !!detailData.outside_tasks.length && (
                  <div
                    style={{
                      border: "solid 1px #b53f3f",
                      marginBottom: "16px",
                    }}
                  >
                    <div
                      style={{
                        backgroundColor: "#b53f3f",
                        color: "#ffffff",
                        padding: "4px 8px",
                        fontSize: "12px",
                        display: "flex",
                        alignItems: "center",
                        gap: "4px",
                        cursor: "pointer",
                      }}
                      onClick={() => setIsOpenOutside((v) => !v)}
                      role="none"
                    >
                      標準勤務時間外に記録された作業データがあります
                      <FontAwesomeIcon icon={['fas', isOpenOutside ? 'angle-up' : 'angle-right']} />
                    </div>
                    {
                      isOpenOutside && (
                        <div
                          style={{
                            display: "flex",
                            flexFlow: "column",
                            gap: "4px",
                            padding: "8px",
                            maxHeight: "100px",
                            overflowY: "scroll",
                          }}
                        >
                          {
                            detailData.outside_tasks.map((v) => (
                              <div
                                key={`outside_${v.task_name}`}
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "space-between",
                                  padding: "4px",
                                  borderBottom: "dashed 1px #CCCCCC",
                                }}
                              >
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "4px",
                                  }}
                                >
                                  <div
                                    style={{
                                      color: v.icon_color || (v.type !== "ActiveTask" && v.task_name ? "#008800" : "#FF0000"),
                                    }}
                                    title={v.service_name}
                                  >
                                    {
                                      v.icon ? (
                                        <FontAwesomeIcon
                                          icon={[v.icon.split(" ")[0] as IconPrefix, v.icon.split(" ")[1] as IconName]}
                                          fixedWidth
                                        />
                                      ) : (
                                        <>
                                          {
                                            v.type === "OnlineMeetingTask" ? (
                                              <FontAwesomeIcon
                                                icon={["fas", "video"]}
                                                fixedWidth
                                              />
                                            ) : (
                                              <FontAwesomeIcon
                                                icon={v.task_name ? ["fas", "check-circle"] : ["fas", "exclamation-triangle"]}
                                                fixedWidth
                                              />
                                            )
                                          }
                                        </>
                                      )
                                    }
                                  </div>
                                  {v.task_name}
                                </div>
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "baseline",
                                    gap: "4px",
                                    fontSize: "8px",
                                  }}
                                >
                                  合計
                                  <div
                                    style={{
                                      fontSize: "14px",
                                    }}
                                  >
                                    {buildSecToTime(v.time)}
                                  </div>
                                  の作業データ
                                </div>
                              </div>
                            ))
                          }
                        </div>
                      )
                    }
                  </div>
                )
              }
              {
                !!detailData.approvers.length && (
                  <div className="flow_area">
                    <div
                      className="flow_area_title"
                      onClick={() => setIsOpenFlow((v) => !v)}
                      role="none"
                    >
                      承認進行状況
                      <FontAwesomeIcon icon={['fas', isOpenFlow ? 'angle-up' : 'angle-right']} />
                    </div>
                    {
                      isOpenFlow && (
                        <>
                          <div className="flow_area_main">
                            {
                              // eslint-disable-next-line max-len
                              sortBy(detailData.approvers, "priority").reverse().map((member, i) => (
                                <React.Fragment key={`${member.member_id}_${member.priority}`}>
                                  <div className="approver_block">
                                    <div className="approver_block_title">
                                      {detailData.approvers.length === i + 1 ? "最終" : `${i + 1}次`}
                                      承認者
                                    </div>
                                    <div className="approver_block_name">
                                      <img
                                        alt="profile"
                                        // eslint-disable-next-line max-len
                                        src={member.profile_image ? member.profile_image : `/static/images/profile/${(Number(member.member_id) || 0) % 12}.jpeg`}
                                        className="profile_icon"
                                      />
                                      <ruby>
                                        {member.name}
                                        <rt>{member.kana}</rt>
                                      </ruby>
                                    </div>
                                    <div
                                      className="approver_block_status"
                                      style={{
                                        color: statusApproverColor(member.status),
                                      }}
                                    >
                                      {buildStatusApprover(member.status)}
                                    </div>
                                  </div>
                                  {
                                    detailData.approvers.length !== i + 1 && (
                                      <FontAwesomeIcon icon={['fas', 'arrow-right']} />
                                    )
                                  }
                                </React.Fragment>
                              ))
                            }
                          </div>
                        </>
                      )
                    }
                  </div>
                )
              }
              {
                isApply && (detailData.is_approvable || isApplication) ? (
                  <>
                    <div className="application_area">
                      <div className="application_area_header">
                        {
                          selectList.length > 1 ? (
                            <>
                              この日の仕分け状態を
                              <select
                                style={{
                                  appearance: "auto",
                                  border: "solid 1px #CCCCCC",
                                  borderRadius: "4px",
                                  padding: "4px 8px",
                                  outline: "none",
                                }}
                                onChange={(e) => setSelectStatus(e.target.value)}
                                defaultValue={selectStatus}
                              >
                                {
                                  selectList.map((v) => (
                                    <option value={v.key}>
                                      {v.text}
                                    </option>
                                  ))
                                }
                              </select>
                              に変更します
                            </>
                          ) : (
                            <>
                              この日の仕分けを
                              <div
                                style={{
                                  fontSize: "16px",
                                }}
                              >
                                {buildStatusWord(selectStatus)}
                              </div>
                              します
                            </>
                          )
                        }
                      </div>
                      <div
                        className="application_area_title"
                        style={{
                          backgroundColor: labelColor(selectStatus),
                        }}
                      >
                        コメント
                      </div>
                      <textarea
                        placeholder="自由入力欄"
                        className="application_area_text"
                        style={{
                          border: `solid 1px ${labelColor(selectStatus)}`,
                        }}
                        onBlur={(e) => setComment(e.target.value)}
                      />
                      {
                        overtime && (
                          <div
                            style={{
                              textAlign: "center",
                              backgroundColor: "#d41515",
                              color: "#FFFFFF",
                              padding: "4px",
                              fontSize: "10px",
                            }}
                          >
                            勤務時間外に仕分け済み作業データが存在します。ご注意ください。
                          </div>
                        )
                      }
                      <div className="button_area">
                        <button
                          type="button"
                          onClick={closeModal}
                          disabled={isSubmitLoading}
                        >
                          閉じる
                        </button>
                        <button
                          type="button"
                          style={{
                            backgroundColor: labelColor(selectStatus),
                            color: "#FFFFFF",
                            border: "none",
                          }}
                          onClick={() => submitApproval()}
                          disabled={isSubmitLoading}
                        >
                          {
                            isSubmitLoading ? (
                              <>
                                <FontAwesomeIcon icon={['fas', 'spinner']} spin />
                              </>
                            ) : (
                              <>
                                {buildStatusWord(selectStatus)}
                              </>
                            )
                          }
                        </button>
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    {
                      !detailData.approvers.length && (
                        <div className="error_area">
                          <p>
                            以下の理由により申請することができません。
                          </p>
                          <p>
                            ・承認グループが設定されていない、または、承認者が設定されていない承認グループに設定されている
                          </p>
                          <p>
                            詳しくは承認フロー設定担当者にご確認ください。
                          </p>
                        </div>
                      )
                    }
                    <div className="button_area">
                      <button
                        type="button"
                        onClick={closeModal}
                      >
                        閉じる
                      </button>
                    </div>
                  </>
                )
              }
            </>
          )
        }
      </>
    </Modal>
  )
}

ApprovedModal.defaultProps = {
  closeable: true,
  isApplication: false,
  displayLink: true,
  moveDateCallback: undefined,
  isDisplayWorkTime: false,
}

export default ApprovedModal
